import React, { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { toast } from "react-toastify"
import axios from "axios"
import { bookNow } from "../../../store/Cab/CabAction"
import { cabAction } from "../../../store/Cab/CAbReducer"
import { setSessionCookie } from "../../../utils/sessionCookie"

/**
 * FindVehicleMain — Silent redirect page (NOT visible to user)
 *
 * Flow:
 *   1. User clicks "Book Hatchback/Sedan/SUV" in WhatsApp carousel
 *   2. Opens: /find-vehicle?token=kayhHABa-S_b&vehicle_id=2&promo=Pulpit10&pickup_city=Mumbai&drop_city=Pune&trip_date=2026-05-23&trip_time=09:00&trip_type=3
 *   3. This page silently:
 *      - Creates session cookie
 *      - Calls visitors_trip_fare API with the token (visitor_unique_id)
 *      - Parses response, picks the cab matching vehicle_id
 *      - Hydrates localStorage(tripData) + Redux
 *      - Dispatches bookNow
 *      - Redirects to /bookCab
 *   4. User only sees a brief loading screen, then lands on /bookCab pre-filled
 *
 * TESTING: Add ?mock=true to URL to use static data (skips API call)
 *   /find-vehicle?mock=true
 *   /find-vehicle?mock=true&vehicle_id=3&promo=Pulpit10
 *
 * API: POST /website/visitors_trip_fare
 *   Request:  { "visitor_unique_id": "kayhHABa-S_b" }
 *   Response: { status, data: [...cabs], visitorEntryId, web_trip_unique_id, distance }
 */

// ─── Static mock data for testing ───────────────────────────
const MOCK_CABS = [
  {
    id: 101,
    vehicle_id: 1,
    name: "Hatchback",
    seat_capacity: 4,
    subtotal: 2500,
    gst: 125,
    gst_percentage: 5,
    total: 2625,
    fare_per_km: 11,
    grand_total: 2625,
    distance: 149,
  },
  {
    id: 102,
    vehicle_id: 2,
    name: "Sedan",
    seat_capacity: 4,
    subtotal: 3200,
    gst: 160,
    gst_percentage: 5,
    total: 3360,
    fare_per_km: 14,
    grand_total: 3360,
    distance: 149,
  },
  {
    id: 103,
    vehicle_id: 3,
    name: "SUV",
    seat_capacity: 6,
    subtotal: 4500,
    gst: 225,
    gst_percentage: 5,
    total: 4725,
    fare_per_km: 18,
    grand_total: 4725,
    distance: 149,
  },
]

const MOCK_TRIP = {
  pickup_city: "Mumbai",
  drop_city: "Pune",
  trip_date: "2026-06-15",
  trip_time: "09:00",
  trip_type: 3,
  round_trip: 0,
  pickup_lat: 19.076,
  pickup_long: 72.8777,
  drop_lat: 18.5204,
  drop_long: 73.8567,
}

const MOCK_DISTANCE = 149
const MOCK_VISITOR_ID = "mock_visitor_001"
const MOCK_TRIP_ID = "mock_token"

/**
 * Normalize cab data from visitors_trip_fare API.
 * The API returns subtotal/gst/total as strings — parse them to numbers.
 * Also adds grand_total (= total) since that field is used downstream.
 */
const normalizeCab = (cab) => ({
  ...cab,
  subtotal: parseFloat(cab.subtotal) || 0,
  gst: parseFloat(cab.gst) || 0,
  total: parseFloat(cab.total) || 0,
  grand_total: parseFloat(cab.total) || 0, // downstream code uses grand_total
  seat_capacity: parseInt(cab.seat_capacity) || 4,
})

const FindVehicleMain = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()

  // ─── Read all URL params ──────────────────────────────────
  const isMock = searchParams.get("mock") === "true"
  const token = searchParams.get("token") || (isMock ? "mock_token" : null)
  const vehicleId = Number(searchParams.get("vehicle_id")) || null
  const promoCode = searchParams.get("promo") || null

  // Trip metadata from URL (WhatsApp automation includes these)
  const pickup_city = searchParams.get("pickup_city") || (isMock ? MOCK_TRIP.pickup_city : "")
  const drop_city = searchParams.get("drop_city") || (isMock ? MOCK_TRIP.drop_city : "")
  const trip_date = searchParams.get("trip_date") || (isMock ? MOCK_TRIP.trip_date : "")
  const trip_time = searchParams.get("trip_time") || (isMock ? MOCK_TRIP.trip_time : "")
  const trip_type = Number(searchParams.get("trip_type")) || (isMock ? MOCK_TRIP.trip_type : 3)

  // ─── Redux state (only for bookNow success tracking) ──────
  const { bookingSuccess, error: cabError } = useSelector((state) => state.cab)

  // ─── Local state for API response ─────────────────────────
  const [apiData, setApiData] = useState(null) // { cabs, visitorEntryId, web_trip_unique_id, distance }
  const [apiError, setApiError] = useState(null)

  // ─── Guards to prevent double-calls ───────────────────────
  const hasCalledApi = useRef(false)
  const hasTriggeredBooking = useRef(false)
  const [errorMessage, setErrorMessage] = useState("")

  // ─── Step 1: Validate & create session ────────────────────
  useEffect(() => {
    if (!token) {
      setErrorMessage("Invalid link. Please search again on our website.")
      toast.error("Invalid link. Please search again.")
      navigate("/", { replace: true })
      return
    }

    // Create session cookie so SessionRouteGuard doesn't block
    setSessionCookie()
  }, [token, navigate])

  // ─── Step 2: Call visitors_trip_fare API ───────────────────
  useEffect(() => {
    if (!token || hasCalledApi.current) return
    hasCalledApi.current = true

    // Mock mode: use static data
    if (isMock) {
      setApiData({
        cabs: MOCK_CABS,
        visitorEntryId: MOCK_VISITOR_ID,
        web_trip_unique_id: MOCK_TRIP_ID,
        distance: MOCK_DISTANCE,
      })
      return
    }

    // Live mode: call the actual API
    const fetchVisitorFare = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/website/visitors_trip_fare`,
          // source/campaign are additive — a backend that doesn't read them
          // ignores them. Only this page knows the customer arrived from the
          // WhatsApp carousel, so if it doesn't say so, nothing can.
          { visitor_unique_id: token, source: "whatsapp", campaign: promoCode || undefined },
          { headers: { "Content-Type": "application/json" } }
        )

        const res = response.data
        if (res.status === 200 && res.data && res.data.length > 0) {
          // Normalize cabs (parse string prices to numbers, add grand_total)
          const normalizedCabs = res.data.map(normalizeCab)

          setApiData({
            cabs: normalizedCabs,
            visitorEntryId: res.visitorEntryId,
            web_trip_unique_id: res.web_trip_unique_id,
            distance: res.distance,
          })
        } else {
          setApiError(res.message || "No cabs found for this trip.")
        }
      } catch (err) {
        console.error("[FindVehicle] API error:", err)
        setApiError(
          err.response?.data?.message || "Something went wrong. Please try again."
        )
      }
    }

    fetchVisitorFare()
  }, [token, isMock, promoCode])

  // ─── Step 3: Handle API error → redirect to home ──────────
  useEffect(() => {
    if (apiError) {
      toast.error(apiError)
      navigate("/", { replace: true })
    }
  }, [apiError, navigate])

  // ─── Step 4: Once API data ready → hydrate → bookNow → redirect ──
  useEffect(() => {
    if (!apiData || hasTriggeredBooking.current) return
    hasTriggeredBooking.current = true

    const { cabs, visitorEntryId, web_trip_unique_id, distance } = apiData

    // Find the cab user clicked in WhatsApp (by vehicle_id)
    const selectedCab =
      cabs.find((c) => c.vehicle_id === vehicleId) || cabs[0]

    // Hydrate localStorage with tripData (exact same format as normal booking flow)
    const tripData = {
      trip_type,
      trip_date,
      trip_time,
      pickup_city,
      drop_city,
      round_trip: 0,
      cab: { ...selectedCab },
      // Promo code stored so PaymentCard can auto-apply it
      ...(promoCode ? { whatsapp_promo: promoCode } : {}),
    }

    localStorage.setItem("tripData", JSON.stringify(tripData))

    // Also store cab search result so other components can read full cab list
    localStorage.setItem(
      "cabSearchResult",
      JSON.stringify({
        data: cabs,
        visitorEntryId,
        web_trip_unique_id,
        distance,
      })
    )

    // Hydrate Redux state so bookNow and PaymentCard work
    dispatch(
      cabAction.setCabSearchResult({
        data: cabs,
        visitorEntryId,
        web_trip_unique_id,
        distance,
      })
    )

    // In mock mode: skip bookNow API call, go directly to /bookCab
    if (isMock) {
      console.log("🧪 [FindVehicle MOCK] tripData hydrated:", tripData)
      console.log("🧪 [FindVehicle MOCK] Selected cab:", selectedCab.name, "(vehicle_id:", selectedCab.vehicle_id, ")")
      console.log("🧪 [FindVehicle MOCK] Promo:", promoCode || "none")
      console.log("🧪 [FindVehicle MOCK] Redirecting to /bookCab...")
      navigate("/bookCab", { replace: true })
      return
    }

    // Live mode: dispatch bookNow to register selection with backend
    const bookingData = {
      visitorEntryId,
      web_trip_unique_id,
      vehicle_id: selectedCab.vehicle_id,
      // `total` is the field the fare API returns; `grand_total` is undefined
      // here and silently stored a NULL fare on the booking.
      grand_total: selectedCab.total,
      advance_booking: 20,
      advance_booking_amount: 500,
      GST: selectedCab.gst_percentage,
      GSTCalculate: selectedCab.gst,
      distance,
      estimate_fare: selectedCab.subtotal,
      source: "whatsapp",
      campaign: promoCode || undefined,
    }

    dispatch(bookNow(bookingData))
  }, [
    apiData,
    isMock,
    vehicleId,
    promoCode,
    trip_type,
    trip_date,
    trip_time,
    pickup_city,
    drop_city,
    dispatch,
    navigate,
  ])

  // ─── Step 5: bookNow success → redirect to /bookCab ──────
  useEffect(() => {
    if (bookingSuccess) {
      dispatch(cabAction.clearBookingSuccess())
      navigate("/bookCab", { replace: true })
    }
  }, [bookingSuccess, dispatch, navigate])

  // ─── Step 6: Handle bookNow error — still redirect ────────
  //     localStorage is already hydrated, so /bookCab will work
  useEffect(() => {
    if (cabError && hasTriggeredBooking.current) {
      navigate("/bookCab", { replace: true })
    }
  }, [cabError, navigate])

  // ─── RENDER: Only a loading screen (user sees this briefly) ──
  return (
    <div className="find-vehicle">
      <div className="find-vehicle__loading">
        <div className="find-vehicle__loading-logo">
          <img
            src="/images/Newlogo.webp"
            alt="Pulpit Cabs"
            className="find-vehicle__logo-img"
          />
        </div>
        <div className="find-vehicle__loading-spinner"></div>
        <h2 className="find-vehicle__loading-title">
          {errorMessage ? "Redirecting..." : "Finding your ride..."}
        </h2>
        <p className="find-vehicle__loading-subtitle">
          {errorMessage || "Hang tight! We're preparing your booking."}
        </p>
      </div>
    </div>
  )
}

export default FindVehicleMain
